import type { CrossroadsLog } from '@/api/hooks/useIntegrationLogs'
import {
  useAcceptOrderChange,
  useRejectOrderChange,
} from '@/api/hooks/useOrderChanges'
import {
  type DataPerspective,
  ORDER_PAGE_SIZE,
  type OrderGridRow,
  type OrderOverviewServerFilters,
  type OrderPerspectiveView,
  fetchOrderFilterValues,
  fetchOrderFind,
  toGridRow,
} from '@/api/hooks/useOrders'
import { useTenantConfig } from '@/api/hooks/useTenantConfig'
import {
  clearRowCountCache,
  useServerSideDataSource,
} from '@/api/useServerSideDatasource/useServerSideDatasource'
import { ControlBarSearch } from '@/components/Display/ControlBarSearch'
import { EnvModeSwitch } from '@/components/Display/EnvModeSwitch'
import { useDebouncedSearch } from '@/pages/Dashboard/panels/ReferenceDataPanel/hooks/useDebouncedSearch'
import { useTenantStore } from '@/stores/tenantStore'
import { type EnvModeSelection, toModeParam } from '@/utils/envMode'
import {
  FileSyncOutlined,
  LoadingOutlined,
  ReloadOutlined,
} from '@ant-design/icons'
import { GraviGrid, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import type {
  GetRowIdParams,
  GridApi,
  ICellRendererParams,
} from 'ag-grid-community'
import {
  Button,
  DatePicker,
  Dropdown,
  Menu,
  Select,
  Tooltip,
  message,
} from 'antd'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { useCallback, useMemo, useRef, useState } from 'react'
import {
  type GetFilterOptions,
  createOrdersColumnDefs,
} from './OrdersColumnDefs'
import { LogDetailsDrawer } from './components/LogDetailsDrawer'
import { OrderChangeRequestDrawer } from './components/OrderChangeRequestDrawer'
import { OrderHistoryDrawer } from './components/OrderHistoryDrawer'
import { OrdersFilterPopover } from './components/OrdersFilterPopover'
import {
  EMPTY_ORDER_FILTERS,
  type PartnerOption,
  toOrderOverviewFilter,
} from './ordersFilterModel'

const { RangePicker } = DatePicker

const UNKNOWN_GROUP_LABEL = '(Unknown)'

/** Set by the backend on every group row -- see label_group_rows in
 * app/order/grid.py. */
const GROUP_LABEL_FIELD = 'groupLabel'

/** Orders always carry a document id; the order number is a readable
 * fallback so a row without one can't collide with its neighbours. SSRM
 * caching and in-place row updates both key off this.
 *
 * Group rows have no id -- `data` is the backend's group summary for the level
 * (e.g. `{ status: 'error', childCount: 2 }`, see paginated_grid/grouping.py).
 * Level and parentKeys alone don't separate siblings at the same depth, so the
 * row's own group-key value goes into the id too. */
function getRowId(params: GetRowIdParams<OrderGridRow>) {
  if (params.data?.id) return params.data.id
  if (params.data && 'childCount' in params.data) {
    const groupKey = Object.entries(params.data)
      .filter(
        ([field]) => field !== 'childCount' && field !== GROUP_LABEL_FIELD,
      )
      .map(([, value]) => String(value))
      .join('|')
    return `group-${params.level}-${params.parentKeys?.join('|') ?? ''}-${groupKey}`
  }
  return `order:${params.data?.internal_order_number ?? ''}`
}

/** Server-side group rows carry no leaf data for AG Grid's default group cell
 * renderer to fall back on, so the label and count come off the row itself.
 *
 * `groupLabel` is the friendly text the backend supplies for the grouped
 * column, matching what its cells and its filter dropdown show -- `node.key`
 * is the raw stored value (`change_requested`), which AG Grid needs verbatim
 * for drill-down but nobody should have to read. */
function GroupInnerRenderer({ node, data }: ICellRendererParams<OrderGridRow>) {
  if (!node.group) return null
  const row = data as
    | { childCount?: number; [GROUP_LABEL_FIELD]?: string }
    | undefined
  return (
    <Horizontal flex={1} alignItems='center'>
      <Texto>
        <b>
          {row?.[GROUP_LABEL_FIELD] || node.key || UNKNOWN_GROUP_LABEL}
          {row?.childCount != null ? ` (${row.childCount})` : ''}
        </b>
      </Texto>
    </Horizontal>
  )
}

// Default date range: last 7 days
const getDefaultDateRange = (): [Dayjs, Dayjs] => [
  dayjs().subtract(7, 'day'),
  dayjs(),
]

export function OrdersPanel() {
  const gridRef = useRef<GridApi>({} as GridApi)

  // Date range state
  const [dateRange, setDateRange] =
    useState<[Dayjs, Dayjs]>(getDefaultDateRange)

  // Log details drawer state -- the log itself comes off the clicked row, so
  // the drawer has nothing to look up before it can render.
  const [selectedLog, setSelectedLog] = useState<{
    log: CrossroadsLog
    logGroupId: string | null
  } | null>(null)
  const selectedTenantName = useTenantStore().selectedTenant?.name

  // Data perspective toggle
  const [dataPerspective, setDataPerspective] =
    useState<DataPerspective>('internal_order')

  // Test/Prod tabs. Defaults to Prod so the grid opens on real traffic; the
  // backend resolves the mode against this tenant's own instances.
  const [mode, setMode] = useState<EnvModeSelection>('Prod')

  // Filters popover state
  const [filters, setFilters] = useState(EMPTY_ORDER_FILTERS)

  // Partners come from the tenant config rather than the loaded rows, so the
  // list doesn't narrow as filters are applied (or keep showing a previous
  // tenant's partners after switching tenants).
  const { data: tenantConfig } = useTenantConfig()
  const partnerOptions = useMemo<PartnerOption[]>(() => {
    if (!tenantConfig) return []
    // Partners are per instance pair, so one partner tenant can repeat.
    const byId = new Map(
      tenantConfig.partners.map(
        (partner) => [partner.tenant_id, partner.tenant_name] as const,
      ),
    )
    return [...byId].map(([value, label]) => ({ value, label }))
  }, [tenantConfig])

  // Everything the query varies by other than the page itself. Memoized
  // because useServerSideDataSource rebuilds the datasource whenever this
  // changes identity -- an inline object would rebuild it every render and
  // knock the grid back to the first page.
  const serverFilters = useMemo<OrderOverviewServerFilters>(
    () => ({
      dateRange: {
        start: dateRange[0].toISOString(),
        end: dateRange[1].toISOString(),
      },
      perspective: dataPerspective,
      mode: toModeParam(mode),
      ...toOrderOverviewFilter(filters),
    }),
    [dateRange, dataPerspective, mode, filters],
  )

  // The search box takes an order number and the backend matches it against
  // every order-number field on both sides of the routing, so it finds an order
  // whether the number typed is this tenant's or the partner's. Debounced, and
  // a change recreates the datasource, which refetches from page one.
  const { searchTerm, setSearchTerm } = useDebouncedSearch()

  const { serverSideDatasource, isFetching, rowCount } =
    useServerSideDataSource(serverFilters, searchTerm, fetchOrderFind)

  // Set-filter dropdown values, reached from the column defs through the grid
  // context. The other active filters and the search term ride along so each
  // dropdown narrows with them.
  const getFilterOptions = useCallback<GetFilterOptions>(
    (column, filterModel) =>
      fetchOrderFilterValues(column, serverFilters, filterModel, searchTerm),
    [serverFilters, searchTerm],
  )

  // Nothing polls under SSRM, so this is the way back to fresh data.
  const handleRefresh = useCallback(() => {
    clearRowCountCache()
    gridRef.current?.refreshServerSide({ purge: true })
  }, [])

  // The grid owns its rows now, so an accepted/rejected change has to be
  // written into the row itself rather than into a cached list.
  const applyRowUpdate = useCallback(
    (row: Parameters<typeof toGridRow>[0] | null | undefined) => {
      if (!row) return
      gridRef.current?.applyServerSideTransaction({
        update: [toGridRow(row, dataPerspective)],
      })
    },
    [dataPerspective],
  )

  const handleLogClick = useCallback(
    (log: CrossroadsLog, logGroupId: string | null) => {
      setSelectedLog({ log, logGroupId })
    },
    [],
  )

  // Order History drawer state
  const [historyOrder, setHistoryOrder] = useState<OrderPerspectiveView | null>(
    null,
  )

  const handleHistoryClick = useCallback((order: OrderPerspectiveView) => {
    setHistoryOrder(order)
  }, [])

  // Change Request drawer state
  const [changeRequestData, setChangeRequestData] = useState<{
    order: OrderPerspectiveView
    changeId: string
  } | null>(null)

  const handleChangeRequestClick = useCallback(
    (changeId: string, order: OrderPerspectiveView) => {
      setChangeRequestData({ order, changeId })
    },
    [],
  )

  // Accept/Reject mutations
  const acceptMutation = useAcceptOrderChange()
  const rejectMutation = useRejectOrderChange()

  const handleAcceptChange = useCallback(() => {
    if (!changeRequestData?.changeId) return

    acceptMutation.mutate(
      { body: { id: changeRequestData.changeId } },
      {
        onSuccess: (data) => {
          applyRowUpdate(data.order_row)
          acceptMutation.invalidateHistory()
          message.success('Order change accepted')
          setChangeRequestData(null)
        },
        onError: (error) => {
          const errorMsg =
            error instanceof Error ? error.message : 'Failed to accept change'
          message.error(errorMsg)
        },
      },
    )
  }, [changeRequestData?.changeId, acceptMutation, applyRowUpdate])

  const handleRejectChange = useCallback(() => {
    if (!changeRequestData?.changeId) return

    rejectMutation.mutate(
      { body: { id: changeRequestData.changeId } },
      {
        onSuccess: (data) => {
          applyRowUpdate(data.order_row)
          rejectMutation.invalidateHistory()
          message.success('Order change rejected')
          setChangeRequestData(null)
        },
        onError: (error) => {
          const errorMsg =
            error instanceof Error ? error.message : 'Failed to reject change'
          message.error(errorMsg)
        },
      },
    )
  }, [changeRequestData?.changeId, rejectMutation, applyRowUpdate])

  // Column definitions
  const columnDefs = useMemo(
    () =>
      createOrdersColumnDefs({
        onLogClick: handleLogClick,
        onHistoryClick: handleHistoryClick,
        onChangeRequestClick: handleChangeRequestClick,
        dateRange: {
          start: dateRange[0].toISOString(),
          end: dateRange[1].toISOString(),
        },
      }),
    [handleLogClick, handleHistoryClick, handleChangeRequestClick, dateRange],
  )

  const agPropOverrides = useMemo(
    () => ({
      context: { getFilterOptions },
      rowModelType: 'serverSide' as const,
      serverSideDatasource,
      pagination: true,
      paginationPageSize: ORDER_PAGE_SIZE,
      // One page is one request to the endpoint.
      cacheBlockSize: ORDER_PAGE_SIZE,
      getRowId,
      rowGroupPanelShow: 'always' as const,
      groupRowRendererParams: {
        innerRenderer: GroupInnerRenderer,
        suppressPadding: true,
        suppressCount: true,
      },
      suppressAggFuncInHeader: true,
      suppressRowClickSelection: true,
      defaultColDef: {
        resizable: true,
        sortable: false,
        filter: false,
      },
      headerHeight: 40,
    }),
    [serverSideDatasource, getFilterOptions],
  )

  const handleDateRangeChange = useCallback(
    (dates: [Dayjs | null, Dayjs | null] | null) => {
      if (dates && dates[0] && dates[1]) {
        setDateRange([dates[0], dates[1]])
      }
    },
    [],
  )

  return (
    <Vertical>
      {/* Orders Grid */}
      <Vertical className='flex-1' style={{ minHeight: 0 }}>
        <GraviGrid
          columnDefs={columnDefs}
          loading={isFetching}
          externalRef={gridRef}
          // Shrinks the action-button region to its content so the search
          // region gets the slack -- it now carries the mode switch too.
          fullWidthSearchBar
          controlBarProps={{
            rowCount,
            title: 'Orders',
            // GraviGrid's own box drives AG Grid's client-side quick filter,
            // which does nothing to rows that live on the server -- this one
            // feeds `search` to the endpoint instead, debounced, so it stays
            // uncontrolled and no `value` is passed.
            customSearchBar: (
              <ControlBarSearch
                onChange={setSearchTerm}
                placeholder='Search order number'
              />
            ),
            actionButtons: (
              <Horizontal alignItems='center' gap={20}>
                <OrdersFilterPopover
                  value={filters}
                  onChange={setFilters}
                  partnerOptions={partnerOptions}
                />
                <EnvModeSwitch value={mode} onChange={setMode} />
                <Horizontal alignItems='center' gap={6}>
                  <Texto appearance='hint' category='label'>
                    Data Format:
                  </Texto>
                  <Select
                    value={dataPerspective}
                    onChange={setDataPerspective}
                    style={{ width: 120 }}
                  >
                    <Select.Option value='internal_order'>
                      Internal
                    </Select.Option>
                    <Select.Option value='gravitate_order'>
                      Gravitate
                    </Select.Option>
                    <Select.Option value='partner_order'>Partner</Select.Option>
                  </Select>
                </Horizontal>
                <RangePicker
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  style={{ width: 240 }}
                  presets={[
                    {
                      label: 'Today',
                      value: [dayjs().startOf('day'), dayjs().endOf('day')],
                    },
                    {
                      label: 'This Week',
                      value: [dayjs().startOf('week'), dayjs().endOf('week')],
                    },
                    {
                      label: 'This Month',
                      value: [dayjs().startOf('month'), dayjs().endOf('month')],
                    },
                    {
                      label: 'Last 3 Days',
                      value: [
                        dayjs().add(-3, 'day').startOf('day'),
                        dayjs().endOf('day'),
                      ],
                    },
                    {
                      label: 'Last Week',
                      value: [
                        dayjs().add(-7, 'day').startOf('day'),
                        dayjs().endOf('day'),
                      ],
                    },
                    {
                      label: 'Last 2 Weeks',
                      value: [
                        dayjs().add(-14, 'day').startOf('day'),
                        dayjs().endOf('day'),
                      ],
                    },
                    {
                      label: 'Last 30 Days',
                      value: [
                        dayjs().add(-30, 'day').startOf('day'),
                        dayjs().endOf('day'),
                      ],
                    },
                  ]}
                />
                <Tooltip title='Refresh'>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={handleRefresh}
                    loading={isFetching}
                  />
                </Tooltip>
                <Tooltip title='Export to Excel'>
                  <Dropdown.Button
                    style={{ width: 'auto' }}
                    overlay={
                      <Menu
                        onClick={() => gridRef.current?.exportDataAsExcel()}
                      >
                        <Menu.Item>Download Excel</Menu.Item>
                      </Menu>
                    }
                    onClick={() => gridRef.current?.exportDataAsExcel()}
                    disabled={isFetching}
                  >
                    {isFetching ? (
                      <LoadingOutlined style={{ fontSize: '1.2em' }} />
                    ) : (
                      <FileSyncOutlined style={{ fontSize: '1.2em' }} />
                    )}
                  </Dropdown.Button>
                </Tooltip>
              </Horizontal>
            ),
          }}
          agPropOverrides={agPropOverrides}
          storageKey='orders-grid-v2' // Don't change
        />
      </Vertical>

      {/* Log Details Drawer */}
      <LogDetailsDrawer
        logGroupId={selectedLog?.logGroupId ?? null}
        log={selectedLog?.log ?? null}
        tenantName={selectedTenantName}
        onClose={() => setSelectedLog(null)}
      />

      {/* Order History Drawer */}
      <OrderHistoryDrawer
        order={historyOrder}
        open={!!historyOrder}
        onClose={() => setHistoryOrder(null)}
      />

      {/* Order Change Request Drawer */}
      <OrderChangeRequestDrawer
        order={changeRequestData?.order ?? null}
        changeId={changeRequestData?.changeId ?? null}
        open={!!changeRequestData}
        onClose={() => setChangeRequestData(null)}
        onAccept={handleAcceptChange}
        onReject={handleRejectChange}
        isAccepting={acceptMutation.isPending}
        isRejecting={rejectMutation.isPending}
      />
    </Vertical>
  )
}
